import type { AnyRoute } from "@tanstack/react-router";
import { isEqual } from "lodash-es";
import { z } from "zod";

import { m } from "@recobra/i18n/messages";
import type { Task, TaskFlowLink } from "@recobra/schemas/task";

/** A page of a task's context a checklist item may link to. */
export type ContextLinkTarget = TaskFlowLink & { key: string; label: string };

const CONTEXT_ROOTS: Record<Task["contextType"], string> = {
  case: "/cases/$caseNumber",
  client: "/clients/$id",
};

/**
 * Every page under the context's own route, one target per value of a page's `tab`
 * search parameter; adding a route or tab adds a target without touching this list.
 */
export const contextLinkTargets = (
  routes: Iterable<AnyRoute>,
  contextType: Task["contextType"],
): ContextLinkTarget[] => {
  const root = CONTEXT_ROOTS[contextType];
  return [...routes]
    .filter(({ fullPath }) => fullPath === root || fullPath.startsWith(`${root}/`))
    .flatMap((route): ContextLinkTarget[] => {
      const to: string = route.fullPath;
      const page = route.options.staticData?.getPageTitle?.() ?? to;
      const tabs = searchTabs(route.options.validateSearch);
      if (tabs.length === 0) return [{ key: to, to, label: page }];
      return tabs.map((tab) => ({
        key: `${to}?tab=${tab}`,
        to,
        search: { tab },
        label: m.task_flow_link_target({ page, tab: m.task_flow_link_tab({ tab }) }),
      }));
    });
};

/** The target a stored link points to, or undefined when the app no longer has that page. */
export const findContextLinkTarget = (
  targets: readonly ContextLinkTarget[],
  link: TaskFlowLink,
): ContextLinkTarget | undefined =>
  targets.find(({ to, search }) => to === link.to && isEqual(search ?? {}, link.search ?? {}));

/** The route parameters a task's context fills in; null when the task cannot link to it. */
export const contextLinkParams = (
  task: Pick<Task, "contextType" | "contextId" | "contextCaseNumber">,
): Record<string, string> | null => {
  if (task.contextType === "client") return { id: task.contextId };
  return task.contextCaseNumber == null ? null : { caseNumber: String(task.contextCaseNumber) };
};

const searchTabs = (validateSearch: unknown): string[] => {
  if (!(validateSearch instanceof z.ZodObject)) return [];
  const tab = validateSearch.shape.tab;
  const values = tab instanceof z.ZodOptional ? tab.unwrap() : tab;
  return values instanceof z.ZodEnum ? values.options.map(String) : [];
};
